// GestionWarehouses.js
import React, { useState, useEffect } from "react";
import InvoiceNumberingSettings from "../components/invoices/InvoiceNumberingSettings";
import {
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Upload,
  notification,
  message,
  Spin,
  Space,
  Row,
  Col,
  Switch,
  Card,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  BankOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import apiClient from "../api/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import SecureLogger from "../utils/secureLogger";
import API_CONFIG from '../config/api.js';
import "./GestionMagasin.css";
import WarehousePaymentRoutes from "./WarehousePaymentRoutes";
import useWarehouseInvoiceQrSetting, { useWarehouseInvoiceSubjectSetting } from "../hooks/useWarehouseInvoiceQrSetting";
import { isInvoiceQrEnabled } from "../utils/invoiceQrVisibility";
import { isInvoiceSubjectEnabled } from "../utils/invoiceSubject";

notification.config({
  duration: 2,
  placement: "topLeft",
  top: window.innerHeight / 2 - 50,
  style: { fontSize: "12px" },
});

const SALES_INVOICE_PRINT_LAYOUT_OPTIONS = [
  {
    value: "a4_single",
    label: "A4 standard — 1 exemplaire par feuille",
  },
  {
    value: "a5_single",
    label: "A5 natif — 1 exemplaire par feuille A5",
  },
  {
    value: "a4_two_a5",
    label: "A4 paysage — 2 exemplaires A5 (client + établissement)",
  },
];

const getSalesInvoicePrintLayoutLabel = (value) =>
  SALES_INVOICE_PRINT_LAYOUT_OPTIONS.find((option) => option.value === value)
    ?.label || SALES_INVOICE_PRINT_LAYOUT_OPTIONS[0].label;

const WarehouseFormSection = ({ id, title, description, children }) => (
  <section
    className="warehouse-form-modal__section"
    aria-labelledby={`${id}-title`}
  >
    <div className="warehouse-form-modal__section-heading">
      <h3 id={`${id}-title`}>{title}</h3>
      {description ? <p>{description}</p> : null}
    </div>
    {children}
  </section>
);

const WarehouseToggleField = ({
  name,
  label,
  description,
  checkedChildren,
  unCheckedChildren,
  disabled = false,
  onChange,
  className = "",
}) => {
  const fieldId = `warehouse-${name}`;
  const descriptionId = description ? `${fieldId}-description` : undefined;

  return (
    <div
      className={`warehouse-form-modal__toggle-field ${className}`.trim()}
    >
      <div className="warehouse-form-modal__toggle-copy">
        <label htmlFor={fieldId}>{label}</label>
        {description ? <span id={descriptionId}>{description}</span> : null}
      </div>
      <Form.Item name={name} valuePropName="checked" noStyle>
        <Switch
          id={fieldId}
          onChange={onChange}
          aria-describedby={descriptionId}
          checkedChildren={checkedChildren}
          unCheckedChildren={unCheckedChildren}
          disabled={disabled}
        />
      </Form.Item>
    </div>
  );
};

const WAREHOUSE_VISIBILITY_LABELS = {
  all: "Tous",
  store: "Établissement uniquement",
  none: "Aucun",
};

const WAREHOUSE_POS_STATUS_LABELS = {
  delivered: "Livré",
  pending: "En attente",
  processing: "En cours",
};

const WAREHOUSE_LOYALTY_PERIOD_LABELS = {
  month: "Mois",
  quarter: "Trimestre",
  year: "Annuel",
  indefinite: "Indéfini",
};

const WarehouseDetailItem = ({ label, children, wide = false }) => (
  <div
    className={`warehouse-detail-modal__item${
      wide ? " warehouse-detail-modal__item--wide" : ""
    }`}
  >
    <span className="warehouse-detail-modal__item-label">{label}</span>
    <div className="warehouse-detail-modal__item-value">{children}</div>
  </div>
);

const WarehouseBooleanTag = ({ value, activeColor = "blue" }) => {
  const isActive = Boolean(Number(value));

  return (
    <Tag
      className="warehouse-detail-modal__status-tag"
      color={isActive ? activeColor : "default"}
    >
      {isActive ? "Oui" : "Non"}
    </Tag>
  );
};

const normalizePaymentRoutesPayload = (routes = []) => {
  if (!Array.isArray(routes)) {
    return [];
  }

  return routes
    .map((route) => ({
      payment_mode_id: Number(route?.payment_mode_id),
      treasury_account_id: Number(route?.treasury_account_id),
    }))
    .filter(
      (route) =>
        Number.isInteger(route.payment_mode_id) &&
        route.payment_mode_id > 0 &&
        Number.isInteger(route.treasury_account_id) &&
        route.treasury_account_id > 0
    );
};

const GestionWarehouses = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const { user, hasPermission } = useAuth();
  const { selectedCompany } = useSelection();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fonction pour construire l'URL du logo (simplifié - le backend envoie déjà des URLs normalisées)
  const getLogoUrl = (logoUrl) => {
    // Le backend envoie maintenant des URLs déjà normalisées (logo_url, dark_logo_url, signature_url)
    // via le helper normalizeImageUrl (même pattern que produits.js)
    if (!logoUrl) return null;

    try {
      // Si c'est déjà une URL complète (Digital Ocean), la retourner telle quelle
      if (logoUrl.startsWith("http")) {
        return logoUrl;
      }

      // Sinon, c'est un chemin local normalisé par le backend, utiliser API_CONFIG
      return logoUrl.startsWith("/") ? logoUrl : API_CONFIG.url(logoUrl);
    } catch (error) {
      SecureLogger.error("Erreur lors de la construction de l'URL du logo");
      return null;
    }
  };

  // Fonction pour prévisualiser un logo
  const handlePreviewLogo = (logoUrl, warehouseName) => {
    setPreviewImage(logoUrl);
    setPreviewVisible(true);
    setPreviewTitle(`Logo de ${warehouseName}`);
  };

  // Fonction pour fermer la prévisualisation
  const handleCancelPreview = () => {
    setPreviewVisible(false);
  };

  // Nouveau filtre dynamique pour les entrepôts
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredWarehouses(warehouses);
    } else {
      const filtered = warehouses.filter(
        (warehouse) =>
          warehouse.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          warehouse.company_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          warehouse.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          warehouse.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          warehouse.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWarehouses(filtered);
    }
  }, [searchTerm, warehouses]);

  const fetchWarehouses = async (page = 1) => {
    try {
      setIsLoading(true);
      if (!selectedCompany) {
        SecureLogger.debug("No company selected, clearing warehouses");
        setWarehouses([]);
        setFilteredWarehouses([]);
        setTotalPages(1);
        setCurrentPage(1);
        setIsLoading(false);
        return;
      }

      // Ajout du préfixe /api explicitement
      SecureLogger.debug(`Fetching warehouses for company ${selectedCompany}`, { page });
      const response = await apiClient.get(
        `/api/warehouses?page=${page}&company_id=${selectedCompany}`
      );
      SecureLogger.debug("Warehouses loaded successfully");
      const warehousesData = response.data.warehouses || [];
      setWarehouses(warehousesData);
      setFilteredWarehouses(warehousesData);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      SecureLogger.error("Erreur lors du chargement des établissements");
      notification.error({
        message: "Erreur de chargement",
        description:
          error.response?.data?.error ||
          "Erreur lors du chargement des établissements",
      });
      setWarehouses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    // Ne charger les entreprises que pour les SysAdmin
    if (!user?.is_superadmin) {
      SecureLogger.debug("Utilisateur normal - pas besoin de charger les entreprises");
      setCompanies([]);
      return false;
    }

    try {
      // Ajout du préfixe /api explicitement
      SecureLogger.debug("Fetching companies for modal");
      const response = await apiClient.get("/api/companies/for-modals");
      SecureLogger.debug("Companies loaded successfully");
      setCompanies(response.data.companies || []);
    } catch (error) {
      SecureLogger.error("Erreur lors du chargement des entreprises");
      notification.error({
        message: "Erreur de chargement",
        description:
          error.response?.data?.error ||
          "Erreur lors du chargement des entreprises",
      });
      setCompanies([]);
    }
  };

  useEffect(() => {
    fetchWarehouses(currentPage);
    fetchCompanies();
  }, [currentPage, selectedCompany]);

  const saveWarehousePaymentRoutes = async (warehouseId, companyId, routes) => {
    await apiClient.put(`/api/warehouses/${warehouseId}/payment-routes`, {
      company_id: companyId, routes_by_module: routes,
    });
  };

  const handleCreateWarehouse = async (formData) => {
    try {
      setIsLoading(true);
      const form = new FormData();
      const { payment_routes: _posPaymentRoutes, ...warehouseValues } =
        formData;

      SecureLogger.debug("[CREATE] Starting warehouse creation");

      // Traitement des champs standards (y compris les nouveaux)
      Object.keys(warehouseValues).forEach((key) => {
        if (!["logo", "dark_logo", "signature"].includes(key)) {
          if (
            warehouseValues[key] !== undefined &&
            warehouseValues[key] !== null
          ) {
            // Conversion des valeurs booléennes en string pour FormData
            const value =
              typeof warehouseValues[key] === "boolean"
                ? warehouseValues[key].toString()
                : warehouseValues[key];
            form.append(key, value);
            SecureLogger.debug(`[CREATE] Field added: ${key}`);
          }
        }
      });

      // Traitement spécifique des images
      if (warehouseValues.logo && warehouseValues.logo instanceof File) {
        form.append("logo", warehouseValues.logo);
        SecureLogger.debug("[CREATE] Logo file added");
      }
      if (
        warehouseValues.dark_logo &&
        warehouseValues.dark_logo instanceof File
      ) {
        form.append("dark_logo", warehouseValues.dark_logo);
        SecureLogger.debug("[CREATE] Dark logo file added");
      }
      if (
        warehouseValues.signature &&
        warehouseValues.signature instanceof File
      ) {
        form.append("signature", warehouseValues.signature);
        SecureLogger.debug("[CREATE] Signature file added");
      }

      // Debug: afficher le nombre de champs dans FormData
      let fieldCount = 0;
      for (let [key, value] of form.entries()) {
        fieldCount++;
      }
      SecureLogger.debug(`[CREATE] FormData contains ${fieldCount} fields`);

      const response = await apiClient.post("/api/warehouses", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      SecureLogger.info("[CREATE] Warehouse created successfully");
      message.success("Établissement créé avec succès");
      await fetchWarehouses(currentPage);
      return true;
    } catch (error) {
      SecureLogger.error("Erreur lors de la création d'établissement");
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          error.response?.data?.details ||
          "Erreur lors de la création de l'établissement",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateWarehouse = async (formData) => {
    if (!selectedWarehouse?.id) {
      notification.error({
        message: "Erreur",
        description: "Aucun établissement sélectionné pour la mise à jour",
      });
      return false;
    }

    try {
      setIsLoading(true);
      const form = new FormData();
      const { payment_routes, ...warehouseValues } = formData;

      SecureLogger.debug("[UPDATE] Starting warehouse update", { warehouseId: selectedWarehouse.id });

      // Traitement des champs standards (y compris les nouveaux)
      Object.keys(warehouseValues).forEach((key) => {
        if (!["logo", "dark_logo", "signature"].includes(key)) {
          if (
            warehouseValues[key] !== undefined &&
            warehouseValues[key] !== null
          ) {
            // Conversion des valeurs booléennes en string pour FormData
            const value =
              typeof warehouseValues[key] === "boolean"
                ? warehouseValues[key].toString()
                : warehouseValues[key];
            form.append(key, value);
            SecureLogger.debug(`[UPDATE] Field added: ${key}`);
          }
        }
      });

      // Traitement spécifique des images
      if (warehouseValues.logo && warehouseValues.logo instanceof File) {
        form.append("logo", warehouseValues.logo);
        SecureLogger.debug("[UPDATE] Logo file added");
      }
      if (
        warehouseValues.dark_logo &&
        warehouseValues.dark_logo instanceof File
      ) {
        form.append("dark_logo", warehouseValues.dark_logo);
        SecureLogger.debug("[UPDATE] Dark logo file added");
      }
      if (
        warehouseValues.signature &&
        warehouseValues.signature instanceof File
      ) {
        form.append("signature", warehouseValues.signature);
        SecureLogger.debug("[UPDATE] Signature file added");
      }

      // Debug: afficher le nombre de champs dans FormData
      let fieldCount = 0;
      for (let [key, value] of form.entries()) {
        fieldCount++;
      }
      SecureLogger.debug(`[UPDATE] FormData contains ${fieldCount} fields`);

      const response = await apiClient.put(
        `/api/warehouses/${selectedWarehouse.id}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (payment_routes && typeof payment_routes === "object") {
        try {
          await saveWarehousePaymentRoutes(
            selectedWarehouse.id,
            selectedWarehouse.company_id,
            payment_routes
          );
        } catch (routeError) {
          await fetchWarehouses(currentPage);
          notification.warning({
            message: "Routage des paiements non enregistré",
            description:
              routeError.response?.data?.error ||
              routeError.response?.data?.details ||
              "L'établissement a été mis à jour, mais le routage des paiements n'a pas pu être enregistré.",
          });
          return false;
        }
      }

      SecureLogger.info("[UPDATE] Warehouse updated successfully");
      message.success("Établissement mis à jour avec succès");
      await fetchWarehouses(currentPage);
      return true;
    } catch (error) {
      SecureLogger.error("Erreur lors de la mise à jour d'établissement");
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          error.response?.data?.details ||
          "Erreur lors de la mise à jour de l'établissement",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteWarehouse = async (warehouse) => {
    if (!warehouse?.id) {
      message.error("ID de l'établissement manquant");
      return;
    }
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet établissement ?")) {
      try {
        setIsLoading(true);
        await apiClient.delete(`/api/warehouses/${warehouse.id}`);
        message.success("Établissement supprimé avec succès");
        if (warehouses.length === 1 && currentPage > 1) {
          setCurrentPage((prev) => prev - 1);
        } else {
          fetchWarehouses(currentPage);
        }
      } catch (error) {
        SecureLogger.error("Erreur lors de la suppression d'établissement");
        notification.error({
          message: "Erreur",
          description:
            error.response?.data?.error ||
            "Erreur lors de la suppression de l'établissement",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleStatus = async (warehouse) => {
    const newStatus = warehouse.status === "active" ? "inactive" : "active";
    try {
      setWarehouses((prev) =>
        prev.map((w) =>
          w.id === warehouse.id ? { ...w, status: newStatus } : w
        )
      );
      await apiClient.patch(`/api/warehouses/${warehouse.id}/status`, {
        status: newStatus,
      });
      message.success(`Statut mis à jour : ${newStatus}`);
    } catch (error) {
      setWarehouses((prev) =>
        prev.map((w) =>
          w.id === warehouse.id ? { ...w, status: warehouse.status } : w
        )
      );
      SecureLogger.error("Erreur lors de la mise à jour du statut");
      notification.error({
        message: "Erreur",
        description:
          error.response?.data?.error ||
          "Erreur lors de la mise à jour du statut",
      });
    }
  };

  // Fonction pour afficher les détails du magasin
  const handleViewWarehouseDetails = (warehouse) => {
    setWarehouseDetails(warehouse);
    setDetailModalVisible(true);
  };

  // Fonction pour fermer le modal de détails
  const handleCloseDetailModal = () => {
    setDetailModalVisible(false);
    setWarehouseDetails(null);
  };

  // Fonction pour obtenir le nom de l'entreprise sélectionnée
  const getSelectedCompanyName = () => {
    if (!selectedCompany || !user?.assigned_warehouses)
      return `Entreprise ID: ${selectedCompany}`;

    const warehouse = user.assigned_warehouses.find(
      (w) => w.company_id === selectedCompany
    );
    return warehouse?.company_name || `Entreprise ID: ${selectedCompany}`;
  };

  const WarehouseModal = ({
    visible,
    onClose,
    warehouse = null,
    companies = [],
    onSubmit,
    user,
    selectedCompany,
    selectedCompanyName,
  }) => {
    const [form] = Form.useForm();
    const [modalPreviewVisible, setModalPreviewVisible] = useState(false);
    const [modalPreviewImage, setModalPreviewImage] = useState("");
    const [modalPreviewTitle, setModalPreviewTitle] = useState("");
    const [paymentRoutingReady, setPaymentRoutingReady] = useState(false);
    const qrValue = Form.useWatch("show_qr_code_on_invoice", form);
    const invoiceQr = useWarehouseInvoiceQrSetting({
      warehouseId: warehouse?.id, companyId: warehouse?.company_id || selectedCompany?.id || selectedCompany,
      userId: user?.id, active: visible, value: qrValue,
      setValue: value => form.setFieldValue("show_qr_code_on_invoice", value),
    });
    const subjectValue = Form.useWatch("invoice_subject_enabled", form);
    const invoiceSubject = useWarehouseInvoiceSubjectSetting({
      warehouseId: warehouse?.id, companyId: warehouse?.company_id || selectedCompany?.id || selectedCompany,
      userId: user?.id, active: visible, value: subjectValue,
      setValue: value => form.setFieldValue("invoice_subject_enabled", value),
    });

    // Fonction pour convertir un fichier en base64 pour la prévisualisation
    const getBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    };

    // Gestion de la prévisualisation des images dans le modal
    const handleModalPreview = async (file) => {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj);
      }
      setModalPreviewImage(file.url || file.preview);
      setModalPreviewVisible(true);
      setModalPreviewTitle(
        file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
      );
    };

    const handleModalCancelPreview = () => setModalPreviewVisible(false);

    useEffect(() => {
      if (visible) {
        if (warehouse) {
          // Préparation des valeurs pour le formulaire
          const fileListLogo = [];
          const fileListDarkLogo = [];
          const fileListSignature = [];

          // Traitement du logo
          if (warehouse.logo) {
            const logoURL = warehouse.logo.startsWith("http")
              ? warehouse.logo
              : warehouse.logo.startsWith("/uploads/")
              ? API_CONFIG.url(warehouse.logo)
              : API_CONFIG.url(`/uploads/warehouses/${warehouse.logo}`);

            fileListLogo.push({
              uid: "-1",
              name: "logo",
              status: "done",
              url: logoURL,
            });
          }

          // Traitement du dark_logo
          if (warehouse.dark_logo) {
            const darkLogoURL = warehouse.dark_logo.startsWith("http")
              ? warehouse.dark_logo
              : warehouse.dark_logo.startsWith("/uploads/")
              ? API_CONFIG.url(warehouse.dark_logo)
              : API_CONFIG.url(`/uploads/warehouses/${warehouse.dark_logo}`);

            fileListDarkLogo.push({
              uid: "-1",
              name: "dark_logo",
              status: "done",
              url: darkLogoURL,
            });
          }

          // Traitement de la signature
          if (warehouse.signature) {
            const signatureURL = warehouse.signature.startsWith("http")
              ? warehouse.signature
              : warehouse.signature.startsWith("/uploads/")
              ? API_CONFIG.url(warehouse.signature)
              : API_CONFIG.url(`/uploads/warehouses/${warehouse.signature}`);

            fileListSignature.push({
              uid: "-1",
              name: "signature",
              status: "done",
              url: signatureURL,
            });
          }

          // Conversion explicite des valeurs booléennes
          const values = {
            name: warehouse.name,
            company_id: warehouse.company_id,
            address: warehouse.address,
            phone: warehouse.phone,
            email: warehouse.email,
            status: warehouse.status === "active",
            // Nouveaux champs légaux
            website: warehouse.website,
            tax_number: warehouse.tax_number,
            rccm_number: warehouse.rccm_number,
            registration_number: warehouse.registration_number,
            capital_social: warehouse.capital_social,
            legal_status: warehouse.legal_status ?? "",
            // Options de facturation
            show_email_on_invoice: Boolean(
              Number(warehouse.show_email_on_invoice)
            ),
            show_phone_on_invoice: Boolean(
              Number(warehouse.show_phone_on_invoice)
            ),
            terms_condition: warehouse.terms_condition,
            bank_details: warehouse.bank_details,
            prefixe_inv: warehouse.prefixe_inv,
            online_store_enabled: Boolean(
              Number(warehouse.online_store_enabled)
            ),
            customers_visibility: warehouse.customers_visibility || "all",
            suppliers_visibility: warehouse.suppliers_visibility || "all",
            products_visibility: warehouse.products_visibility || "all",
            default_pos_order_status:
              warehouse.default_pos_order_status || "delivered",
            show_mrp_on_invoice: Boolean(Number(warehouse.show_mrp_on_invoice)),
            show_discount_tax_on_invoice: Boolean(
              Number(warehouse.show_discount_tax_on_invoice)
            ),
            show_qr_code_on_invoice: isInvoiceQrEnabled(warehouse),
            invoice_subject_enabled: isInvoiceSubjectEnabled(warehouse),
            show_qr_code_on_receipt: Boolean(
              Number(
                warehouse.show_qr_code_on_receipt !== undefined
                  ? warehouse.show_qr_code_on_receipt
                  : 1
              )
            ),
            sales_invoice_print_layout:
              warehouse.sales_invoice_print_layout || "a4_single",
            loyalty_program_enabled: Boolean(
              Number(warehouse.loyalty_program_enabled)
            ),
            loyalty_min_periods: Number(warehouse.loyalty_min_periods || 0),
            loyalty_period_type: warehouse.loyalty_period_type || "month",
            loyalty_discount_rate: Number(warehouse.loyalty_discount_rate || 0),
            loyalty_prompt_on_sale: Boolean(
              Number(
                warehouse.loyalty_prompt_on_sale !== undefined
                  ? warehouse.loyalty_prompt_on_sale
                  : 1
              )
            ),
            payment_routes: undefined,
            logo: fileListLogo,
            dark_logo: fileListDarkLogo,
            signature: fileListSignature,
          };
          form.setFieldsValue(values);
          SecureLogger.debug("[Modal] Values loaded for warehouse modification");
        } else {
          // Nouveau entrepôt - pré-remplir avec l'entreprise sélectionnée pour les utilisateurs normaux
          const defaultValues = {
            status: true, // Actif par défaut
            legal_status: "",
            customers_visibility: "all",
            suppliers_visibility: "all",
            products_visibility: "all",
            default_pos_order_status: "delivered",
            show_email_on_invoice: false,
            show_phone_on_invoice: false,
            show_mrp_on_invoice: true,
            show_discount_tax_on_invoice: true,
            show_qr_code_on_invoice: true,
            invoice_subject_enabled: false,
            show_qr_code_on_receipt: true,
            sales_invoice_print_layout: "a4_single",
            online_store_enabled: true,
            loyalty_program_enabled: false,
            loyalty_min_periods: 0,
            loyalty_period_type: "month",
            loyalty_discount_rate: 0,
            loyalty_prompt_on_sale: true,
            payment_routes: undefined,
          };

          // Pour les utilisateurs normaux, pré-remplir avec l'entreprise sélectionnée
          if (!user?.is_superadmin && selectedCompany) {
            defaultValues.company_id = selectedCompany;
          }

          form.resetFields();
          form.setFieldsValue(defaultValues);
          SecureLogger.debug("[Modal] Default values set for new warehouse");
        }
      }
    }, [visible, warehouse, form, user?.is_superadmin, selectedCompany]);

    const handleOk = async () => {
      try {
        const values = await form.validateFields();
        SecureLogger.debug("[Modal] Form values submitted");
        const processedValues = { ...values };
        delete processedValues.show_qr_code_on_invoice;
        Object.assign(processedValues, await invoiceQr.prepare());
        delete processedValues.invoice_subject_enabled;
        Object.assign(processedValues, await invoiceSubject.prepare());

          // Gérer les fichiers d'images
          ["logo", "dark_logo", "signature"].forEach((field) => {
            if (
              values[field] &&
              Array.isArray(values[field]) &&
              values[field].length > 0
            ) {
              // Si c'est un tableau d'objets fileList (comme retourné par Upload)
              const fileObj = values[field][0];
              if (fileObj && fileObj.originFileObj) {
                processedValues[field] = fileObj.originFileObj;
                SecureLogger.debug(`[Modal] File ${field} processed`);
              } else if (fileObj && fileObj.status === "done" && fileObj.url) {
                // Fichier existant, ne pas inclure dans les données à envoyer
                delete processedValues[field];
                SecureLogger.debug(`[Modal] Existing file ${field} kept`);
              } else {
                delete processedValues[field];
                SecureLogger.debug(`[Modal] Invalid file ${field}`);
              }
            } else if (
              values[field] &&
              values[field].fileList &&
              values[field].fileList.length > 0
            ) {
              // Si c'est un objet avec une propriété fileList
              const fileObj = values[field].fileList[0];
              if (fileObj && fileObj.originFileObj) {
                processedValues[field] = fileObj.originFileObj;
                SecureLogger.debug(`[Modal] File ${field} processed`);
              } else if (fileObj && fileObj.status === "done" && fileObj.url) {
                // Fichier existant, ne pas inclure dans les données à envoyer
                delete processedValues[field];
                SecureLogger.debug(`[Modal] Existing file ${field} kept`);
              } else {
                delete processedValues[field];
                SecureLogger.debug(`[Modal] Invalid file ${field}`);
              }
            } else {
              // Si aucun fichier n'est sélectionné, supprimer la propriété
              delete processedValues[field];
              SecureLogger.debug(`[Modal] No file ${field} selected`);
            }
          });

        if (paymentRoutingReady) {
          processedValues.payment_routes = Object.fromEntries(Object.entries(values.payment_routes || {}).map(([scope, routes]) => [scope, normalizePaymentRoutesPayload(routes)]));
        } else {
          delete processedValues.payment_routes;
        }

        // Le cycle de vie d'un Warehouse est une décision SysAdmin.
        if (user?.is_superadmin) {
          processedValues.status = values.status ? "active" : "inactive";
        } else {
          delete processedValues.status;
        }

        // S'assurer que company_id est fourni
        if (!processedValues.company_id && !warehouse) {
          message.error("Veuillez sélectionner une entreprise");
          return;
        }

        SecureLogger.debug("[Modal] Sending processed values to backend");
        const shouldClose = await onSubmit(processedValues);
        if (shouldClose !== false) {
          await invoiceQr.saved();
          onClose();
          form.resetFields();
        }
      } catch (error) {
        SecureLogger.debug("Form validation failed");
      }
    };

    const handleClose = () => {
      onClose();
      form.resetFields();
    };

    return (
      <Modal
        key={warehouse ? warehouse.id : "new"} // force un re-rendu quand l'ID change
        open={visible}
        centered
        className="warehouse-form-modal"
        title={
          <div className="warehouse-form-modal__header">
            <span className="warehouse-form-modal__header-icon" aria-hidden="true">
              <BankOutlined />
            </span>
            <div className="warehouse-form-modal__header-copy">
              <div className="warehouse-form-modal__heading">
                {warehouse ? "Modifier l'établissement" : "Créer un établissement"}
              </div>
              <div className="warehouse-form-modal__subtitle">
                {warehouse
                  ? "Mettez à jour les informations et les paramètres de cet établissement."
                  : "Renseignez les informations et configurez le nouvel établissement."}
              </div>
            </div>
          </div>
        }
        onCancel={handleClose}
        footer={
          <div className="warehouse-form-modal__footer">
            <Button disabled={isLoading} onClick={handleClose}>
              Annuler
            </Button>
            <Button type="primary" loading={isLoading} onClick={handleOk}>
              {warehouse ? "Enregistrer les modifications" : "Créer l'établissement"}
            </Button>
          </div>
        }
        width={1280}
        destroyOnClose
      >
        <Form
          className="warehouse-form-modal__form"
          form={form}
          layout="vertical"
        >
          <WarehouseFormSection
            id="warehouse-general"
            title="Informations générales"
            description="Identité, rattachement et coordonnées principales de l'établissement."
          >
            <Row gutter={[20, 0]}>
              <Col xs={24} md={12} xl={8}>
              <Form.Item
                label="Nom de l'établissement"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Le nom de l'établissement est requis",
                  },
                ]}
              >
                <Input placeholder="Nom de l'établissement" />
              </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
              <Form.Item
                label="Entreprise"
                name="company_id"
                rules={[
                  { required: true, message: "L'entreprise est requise" },
                ]}
              >
                {user?.is_superadmin ? (
                  <Select
                    placeholder="Sélectionnez une entreprise"
                    showSearch
                    optionFilterProp="children"
                  >
                    {companies.map((comp) => (
                      <Select.Option key={comp.id} value={comp.id}>
                        {comp.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Select
                    placeholder="Entreprise sélectionnée"
                    disabled
                    value={selectedCompany}
                  >
                    {selectedCompany && (
                      <Select.Option value={selectedCompany}>
                        {selectedCompanyName}
                      </Select.Option>
                    )}
                  </Select>
                )}
              </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <WarehouseToggleField
                  className="warehouse-form-modal__toggle-field--status"
                  name="status"
                  label="Statut de l'établissement"
                  description="Autoriser l'utilisation de cet établissement."
                  checkedChildren="Actif"
                  unCheckedChildren="Inactif"
                  disabled={!user?.is_superadmin}
                />
              </Col>
              <Col xs={24} md={12} xl={12}>
              <Form.Item
                label="Adresse"
                name="address"
                rules={[{ required: true, message: "L'adresse est requise" }]}
              >
                <Input.TextArea rows={2} placeholder="Adresse de l'établissement" />
              </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={6}>
              <Form.Item label="Téléphone" name="phone">
                <Input placeholder="Téléphone" />
              </Form.Item>
              </Col>
              <Col xs={24} sm={12} xl={6}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: "email", message: "Email invalide" }]}
              >
                <Input placeholder="Email" />
              </Form.Item>
              </Col>
            </Row>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-legal"
            title="Informations légales"
            description="Références administratives utilisées sur les documents commerciaux."
          >
            <Row gutter={[20, 0]}>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="website" label="Site Web">
                  <Input placeholder="https://www.example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="tax_number" label="N° IFU / TVA">
                  <Input placeholder="Numéro d'Identification Fiscale Unique" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="rccm_number" label="N° RCCM">
                  <Input placeholder="Registre du Commerce et du Crédit Mobilier" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item
                  name="registration_number"
                  label="N° Registre du Commerce (RC)"
                >
                  <Input placeholder="Numéro du Registre du Commerce" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name="capital_social" label="Capital Social">
                  <Input placeholder="Ex: 1 000 000 FCFA" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item
                  name="legal_status"
                  label="Statut Juridique"
                  normalize={(value) => value ?? ""}
                  extra="Facultatif. Laissez vide pour ne pas l'afficher sur les documents."
                >
                  <Select allowClear placeholder="Non renseigné">
                    <Select.Option value="">Non renseigné</Select.Option>
                    <Select.Option value="SARL">SARL</Select.Option>
                    <Select.Option value="SA">SA</Select.Option>
                    <Select.Option value="SAS">SAS</Select.Option>
                    <Select.Option value="SNC">SNC</Select.Option>
                    <Select.Option value="EI">
                      Entreprise Individuelle
                    </Select.Option>
                    <Select.Option value="EURL">EURL</Select.Option>
                    <Select.Option value="GIE">GIE</Select.Option>
                    <Select.Option value="Autre">Autre</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-invoicing"
            title="Facturation et impression"
            description="Choisissez les informations visibles et le format des documents de vente."
          >
            <div className="warehouse-form-modal__toggle-grid">
              <WarehouseToggleField
                name="show_email_on_invoice"
                label="Afficher l'email sur la facture"
              />
              <WarehouseToggleField
                name="show_phone_on_invoice"
                label="Afficher le N° téléphone sur la facture"
              />
              <WarehouseToggleField
                name="show_mrp_on_invoice"
                label="Afficher le MRP sur la facture"
              />
              <WarehouseToggleField
                name="show_discount_tax_on_invoice"
                label="Afficher les remises et taxes sur la facture"
              />
              <WarehouseToggleField
                name="show_qr_code_on_invoice"
                label="Afficher le QR code sur les factures de vente"
                description={invoiceQr.error || (invoiceQr.ready ? "Permet de vérifier la facture. Réglage indépendant du ticket de caisse." : "Actualisation du réglage QR…")}
                disabled={!invoiceQr.ready}
                onChange={invoiceQr.onChange}
              />
              {invoiceQr.error ? <Button onClick={invoiceQr.reload}>Recharger le réglage QR</Button> : null}
              <WarehouseToggleField
                name="invoice_subject_enabled"
                label="Autoriser un objet sur les factures de vente"
                description={invoiceSubject.error || (invoiceSubject.ready ? "Champ facultatif à la facturation, imprimé sous les coordonnées du client." : "Actualisation du réglage objet…")}
                disabled={!invoiceSubject.ready}
                onChange={invoiceSubject.onChange}
              />
              {invoiceSubject.error ? <Button onClick={invoiceSubject.reload}>Recharger le réglage objet</Button> : null}
              <WarehouseToggleField
                name="show_qr_code_on_receipt"
                label="Afficher le QR code sur le ticket de caisse"
              />
            </div>

            {warehouse?.id && <InvoiceNumberingSettings companyId={warehouse.company_id} warehouseId={warehouse.id} active={visible} />}

            <Row gutter={[20, 0]}>
              <Col xs={24} md={16}>
                <Form.Item
                  label="Format d'impression des factures de vente"
                  name="sales_invoice_print_layout"
                  tooltip="Le format A5 natif crée une page PDF au format physique A5. Le format à deux exemplaires place automatiquement les copies client et établissement côte à côte sur une feuille A4 paysage."
                >
                  <Select options={SALES_INVOICE_PRINT_LAYOUT_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Préfixe de facturation"
                  name="prefixe_inv"
                  tooltip="Préfixe utilisé pour les numéros de facture générés par cet établissement (ex: MAG1-)"
                  rules={[
                    {
                      max: 10,
                      message: "Le préfixe ne doit pas dépasser 10 caractères",
                    },
                  ]}
                >
                  <Input
                    placeholder="Préfixe de facturation (ex: MAG1-)"
                    maxLength={10}
                  />
                </Form.Item>
              </Col>
            </Row>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-loyalty"
            title="Programme de fidélité"
            description="Définissez les conditions d'accès à la remise fidélité."
          >
            <WarehouseToggleField
              className="warehouse-form-modal__toggle-field--featured"
              name="loyalty_program_enabled"
              label="Activer le programme de fidélité"
              description="Appliquer automatiquement les règles ci-dessous aux clients éligibles."
            />

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.loyalty_program_enabled !==
                currentValues.loyalty_program_enabled
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("loyalty_program_enabled") ? (
                  <div className="warehouse-form-modal__conditional-fields">
                    <Row gutter={[20, 0]}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Nombre minimum de commandes"
                          name="loyalty_min_periods"
                          rules={[
                            {
                              required: true,
                              message: "Renseignez le nombre de commandes requis",
                            },
                          ]}
                        >
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Type de période"
                          name="loyalty_period_type"
                          rules={[
                            {
                              required: true,
                              message: "Sélectionnez le type de période",
                            },
                          ]}
                        >
                          <Select>
                            <Select.Option value="month">Mois</Select.Option>
                            <Select.Option value="quarter">Trimestre</Select.Option>
                            <Select.Option value="year">Annuel</Select.Option>
                            <Select.Option value="indefinite">Indéfini</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Taux de remise fidélité (%)"
                          name="loyalty_discount_rate"
                          rules={[
                            {
                              required: true,
                              message: "Renseignez un taux de remise",
                            },
                          ]}
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            step={0.01}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <WarehouseToggleField
                      name="loyalty_prompt_on_sale"
                      label="Afficher une confirmation pendant la vente"
                    />
                  </div>
                ) : null
              }
            </Form.Item>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-commercial-settings"
            title="Documents et règles commerciales"
            description="Complétez les mentions imprimées et définissez la portée des données de l'établissement."
          >
            <Row gutter={[20, 0]}>
              <Col xs={24} md={12}>
                <Form.Item label="Conditions générales" name="terms_condition">
                  <Input.TextArea rows={3} placeholder="Conditions générales" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Informations bancaires" name="bank_details">
                  <Input.TextArea rows={3} placeholder="Informations bancaires" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item
                  label="Visibilité des clients"
                  name="customers_visibility"
                >
                  <Select>
                    <Select.Option value="all">Tous</Select.Option>
                    <Select.Option value="store">
                      Établissement uniquement
                    </Select.Option>
                    <Select.Option value="none">Aucun</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item
                  label="Visibilité des fournisseurs"
                  name="suppliers_visibility"
                >
                  <Select>
                    <Select.Option value="all">Tous</Select.Option>
                    <Select.Option value="store">
                      Établissement uniquement
                    </Select.Option>
                    <Select.Option value="none">Aucun</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item
                  label="Visibilité des produits"
                  name="products_visibility"
                >
                  <Select>
                    <Select.Option value="all">Tous</Select.Option>
                    <Select.Option value="store">
                      Établissement uniquement
                    </Select.Option>
                    <Select.Option value="none">Aucun</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={6}>
                <Form.Item
                  label="Statut par défaut des commandes POS"
                  name="default_pos_order_status"
                >
                  <Select>
                    <Select.Option value="delivered">Livré</Select.Option>
                    <Select.Option value="pending">En attente</Select.Option>
                    <Select.Option value="processing">En cours</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-payment-routing"
            title="Routage des paiements"
            description="Associez chaque mode de paiement au compte de son module et de cet établissement."
          >
            <WarehousePaymentRoutes form={form} warehouse={warehouse} companies={companies} user={user}
              visible={visible} onReadyChange={setPaymentRoutingReady} />
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-branding"
            title="Identité visuelle"
            description="Ajoutez les éléments utilisés sur les factures, reçus et autres documents."
          >
            <Row gutter={[20, 0]}>
              <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Logo"
                name="logo"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e) ? e : e && e.fileList
                }
              >
                <Upload
                  name="logo"
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={handleModalPreview}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Importer</div>
                  </div>
                </Upload>
              </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Logo sombre"
                name="dark_logo"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e) ? e : e && e.fileList
                }
              >
                <Upload
                  name="dark_logo"
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={handleModalPreview}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Importer</div>
                  </div>
                </Upload>
              </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Signature"
                name="signature"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e) ? e : e && e.fileList
                }
              >
                <Upload
                  name="signature"
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false}
                  onPreview={handleModalPreview}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Importer</div>
                  </div>
                </Upload>
              </Form.Item>
              </Col>
            </Row>
          </WarehouseFormSection>
        </Form>
        <Modal
          visible={modalPreviewVisible}
          title={modalPreviewTitle}
          footer={null}
          onCancel={handleModalCancelPreview}
          width={isSmallScreen ? "95%" : undefined} // Responsive width for internal preview modal
        >
          <img
            alt="Preview"
            style={{ width: "100%" }}
            src={modalPreviewImage}
          />
        </Modal>
      </Modal>
    );
  };

  // Composant pour le modal de détails
  const WarehouseDetailModal = ({ visible, onClose, warehouse }) => {
    if (!warehouse) return null;

    // Utiliser les URLs déjà normalisées par le backend
    const logoURL = warehouse.logo_url || null;
    const darkLogoURL = warehouse.dark_logo_url || null;
    const signatureURL = warehouse.signature_url || null;
    const hasCommercialDocuments = Boolean(
      warehouse.terms_condition || warehouse.bank_details
    );
    const hasAdditionalVisuals = Boolean(darkLogoURL || signatureURL);

    return (
      <Modal
        open={visible}
        centered
        className="warehouse-form-modal warehouse-detail-modal"
        title={
          <div className="warehouse-form-modal__header">
            <span className="warehouse-form-modal__header-icon" aria-hidden="true">
              <EyeOutlined />
            </span>
            <div className="warehouse-form-modal__header-copy">
              <div className="warehouse-form-modal__heading">
                Détails de l'établissement
              </div>
              <div className="warehouse-form-modal__subtitle">
                {warehouse.name}
                {warehouse.company_name ? ` · ${warehouse.company_name}` : ""}
              </div>
            </div>
          </div>
        }
        onCancel={onClose}
        footer={
          <div className="warehouse-form-modal__footer">
            <Button type="primary" onClick={onClose}>
              Fermer
            </Button>
          </div>
        }
        width={1200}
        destroyOnClose
      >
        <div className="warehouse-detail-modal__content">
          <WarehouseFormSection
            id="warehouse-detail-general"
            title="Informations générales"
            description="Identité, rattachement et coordonnées de l'établissement."
          >
            <div
              className={`warehouse-detail-modal__identity${
                logoURL ? "" : " warehouse-detail-modal__identity--without-logo"
              }`}
            >
              {logoURL ? (
                <figure className="warehouse-detail-modal__logo-card">
                  <div className="warehouse-detail-modal__logo-frame">
                    <img
                      src={logoURL}
                      alt={`Logo de ${warehouse.name}`}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src =
                          "https://via.placeholder.com/160?text=Logo";
                      }}
                    />
                  </div>
                  <figcaption>Logo principal</figcaption>
                </figure>
              ) : null}

              <div className="warehouse-detail-modal__details-grid">
                <WarehouseDetailItem label="Nom">
                  {warehouse.name}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Entreprise">
                  {warehouse.company_name || "Non spécifiée"}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="E-mail">
                  <span className="warehouse-detail-modal__inline-value">
                    <MailOutlined aria-hidden="true" />
                    <span>{warehouse.email || "Non spécifié"}</span>
                  </span>
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Téléphone">
                  <span className="warehouse-detail-modal__inline-value">
                    <PhoneOutlined aria-hidden="true" />
                    <span>{warehouse.phone || "Non spécifié"}</span>
                  </span>
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Adresse">
                  <span className="warehouse-detail-modal__inline-value">
                    <EnvironmentOutlined aria-hidden="true" />
                    <span>{warehouse.address || "Non spécifiée"}</span>
                  </span>
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Statut">
                  <Tag
                    className="warehouse-detail-modal__status-tag"
                    color={warehouse.status === "active" ? "green" : "red"}
                  >
                    {warehouse.status === "active" ? "Actif" : "Inactif"}
                  </Tag>
                </WarehouseDetailItem>
              </div>
            </div>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-detail-legal"
            title="Informations légales"
            description="Références administratives figurant sur les documents commerciaux."
          >
            <div className="warehouse-detail-modal__details-grid warehouse-detail-modal__details-grid--three">
              <WarehouseDetailItem label="Site Web">
                {warehouse.website ? (
                  <a
                    href={warehouse.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {warehouse.website}
                  </a>
                ) : (
                  "N/A"
                )}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="N° IFU / TVA">
                {warehouse.tax_number || "N/A"}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="N° RCCM">
                {warehouse.rccm_number || "N/A"}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="N° Registre du Commerce">
                {warehouse.registration_number || "N/A"}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Capital Social">
                {warehouse.capital_social || "N/A"}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Statut Juridique">
                {warehouse.legal_status || "N/A"}
              </WarehouseDetailItem>
            </div>
          </WarehouseFormSection>

          <WarehouseFormSection
            id="warehouse-detail-invoicing"
            title="Facturation et impression"
            description="Informations visibles et format appliqué aux documents de vente."
          >
            <div className="warehouse-detail-modal__details-grid warehouse-detail-modal__details-grid--three">
              <WarehouseDetailItem label="Afficher l'e-mail">
                <WarehouseBooleanTag value={warehouse.show_email_on_invoice} />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Afficher le téléphone">
                <WarehouseBooleanTag value={warehouse.show_phone_on_invoice} />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Afficher le MRP">
                <WarehouseBooleanTag value={warehouse.show_mrp_on_invoice} />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Afficher les remises et taxes">
                <WarehouseBooleanTag
                  value={warehouse.show_discount_tax_on_invoice}
                />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Afficher le QR code sur les factures de vente">
                <WarehouseBooleanTag value={isInvoiceQrEnabled(warehouse)} />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Afficher le QR code sur le ticket">
                <WarehouseBooleanTag
                  value={
                    warehouse.show_qr_code_on_receipt !== undefined
                      ? warehouse.show_qr_code_on_receipt
                      : 1
                  }
                />
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Préfixe de facturation">
                {warehouse.prefixe_inv || "Non défini"}
              </WarehouseDetailItem>
              <WarehouseDetailItem label="Format des factures de vente" wide>
                {getSalesInvoicePrintLayoutLabel(
                  warehouse.sales_invoice_print_layout
                )}
              </WarehouseDetailItem>
            </div>
          </WarehouseFormSection>

          <div className="warehouse-detail-modal__section-grid">
            <WarehouseFormSection
              id="warehouse-detail-visibility"
              title="Paramètres de visibilité"
              description="Portée des données accessibles depuis cet établissement."
            >
              <div className="warehouse-detail-modal__details-grid">
                <WarehouseDetailItem label="Clients">
                  {WAREHOUSE_VISIBILITY_LABELS[
                    warehouse.customers_visibility
                  ] || "Aucun"}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Fournisseurs">
                  {WAREHOUSE_VISIBILITY_LABELS[
                    warehouse.suppliers_visibility
                  ] || "Aucun"}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Produits">
                  {WAREHOUSE_VISIBILITY_LABELS[
                    warehouse.products_visibility
                  ] || "Aucun"}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Statut par défaut des commandes POS">
                  {WAREHOUSE_POS_STATUS_LABELS[
                    warehouse.default_pos_order_status
                  ] || "En cours"}
                </WarehouseDetailItem>
              </div>
            </WarehouseFormSection>

            <WarehouseFormSection
              id="warehouse-detail-loyalty"
              title="Programme de fidélité"
              description="Éligibilité et remise configurées pour les clients."
            >
              <div className="warehouse-detail-modal__details-grid">
                <WarehouseDetailItem label="Programme activé">
                  <WarehouseBooleanTag
                    value={warehouse.loyalty_program_enabled}
                    activeColor="green"
                  />
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Commandes minimales">
                  {Number(warehouse.loyalty_min_periods || 0)}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Type de période">
                  {WAREHOUSE_LOYALTY_PERIOD_LABELS[
                    warehouse.loyalty_period_type
                  ] || "Mois"}
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Taux de remise">
                  {Number(warehouse.loyalty_discount_rate || 0)} %
                </WarehouseDetailItem>
                <WarehouseDetailItem label="Confirmation pendant la vente" wide>
                  <WarehouseBooleanTag
                    value={warehouse.loyalty_prompt_on_sale}
                  />
                </WarehouseDetailItem>
              </div>
            </WarehouseFormSection>
          </div>

          {hasCommercialDocuments ? (
            <WarehouseFormSection
              id="warehouse-detail-documents"
              title="Mentions commerciales"
              description="Textes complémentaires imprimés ou communiqués aux clients."
            >
              <div className="warehouse-detail-modal__document-grid">
                {warehouse.terms_condition ? (
                  <article className="warehouse-detail-modal__document-card">
                    <h4>
                      <FileTextOutlined aria-hidden="true" />
                      Conditions générales
                    </h4>
                    <div>{warehouse.terms_condition}</div>
                  </article>
                ) : null}
                {warehouse.bank_details ? (
                  <article className="warehouse-detail-modal__document-card">
                    <h4>
                      <BankOutlined aria-hidden="true" />
                      Informations bancaires
                    </h4>
                    <div>{warehouse.bank_details}</div>
                  </article>
                ) : null}
              </div>
            </WarehouseFormSection>
          ) : null}

          {hasAdditionalVisuals ? (
            <WarehouseFormSection
              id="warehouse-detail-visuals"
              title="Identité visuelle complémentaire"
              description="Éléments graphiques utilisés selon le support d'impression."
            >
              <div className="warehouse-detail-modal__visual-grid">
                {darkLogoURL ? (
                  <figure className="warehouse-detail-modal__visual-card">
                    <figcaption>Logo sombre</figcaption>
                    <div className="warehouse-detail-modal__visual-frame warehouse-detail-modal__visual-frame--dark">
                      <img
                        src={darkLogoURL}
                        alt={`Logo sombre de ${warehouse.name}`}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src =
                            "https://via.placeholder.com/160?text=Logo";
                        }}
                      />
                    </div>
                  </figure>
                ) : null}
                {signatureURL ? (
                  <figure className="warehouse-detail-modal__visual-card">
                    <figcaption>Signature</figcaption>
                    <div className="warehouse-detail-modal__visual-frame">
                      <img
                        src={signatureURL}
                        alt={`Signature de ${warehouse.name}`}
                        onError={(event) => {
                          event.currentTarget.onerror = null;
                          event.currentTarget.src =
                            "https://via.placeholder.com/160?text=Signature";
                        }}
                      />
                    </div>
                  </figure>
                ) : null}
              </div>
            </WarehouseFormSection>
          ) : null}
        </div>
      </Modal>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Space
          direction="vertical"
          style={{ width: "100%", marginBottom: "20px" }}
        >
          <Typography.Title level={3}>Gestion des Établissements</Typography.Title>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {user?.is_superadmin && hasPermission("Admin.Magasins.create") && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedWarehouse(null);
                  setIsModalOpen(true);
                }}
                className="w-full md:w-auto" // Responsive width
              >
                Créer un établissement
              </Button>
            )}

            {/* Champ de recherche pour filtrer dynamiquement */}
            <Input
              placeholder="Rechercher un établissement..."
              prefix={<SearchOutlined />}
              className="w-full md:w-auto md:max-w-sm" // Responsive width
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </Space>
        {!selectedCompany ? (
          <Card>
            <Typography.Text type="secondary">
              Veuillez sélectionner une entreprise pour voir les établissements
              associés.
            </Typography.Text>
          </Card>
        ) : isLoading ? (
          <Spin tip="Chargement..." />
        ) : (
          <Table
            dataSource={filteredWarehouses}
            rowKey="id"
            pagination={{
              current: currentPage,
              total: totalPages * 10,
              onChange: (page) => setCurrentPage(page),
            }}
            rowClassName={() => "warehouse-table-row"}
            className="warehouse-table"
            size="middle"
            scroll={{ x: "max-content" }}
          >
            <Table.Column
              title="Logo"
              dataIndex="logo_url"
              key="logo"
              width={150}
              className="logo-column"
              render={(logoUrl, record) => {
                if (!logoUrl) return "No logo";

                return (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={logoUrl}
                      alt="Logo"
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        marginRight: 8,
                        borderRadius: "4px",
                        border: "1px solid #e8e8e8",
                      }}
                      onError={(e) => {
                        SecureLogger.debug(`Logo loading error for warehouse: ${record.name}`);
                        e.target.onerror = null;
                        e.target.src =
                          "https://via.placeholder.com/40?text=No+Image";
                      }}
                    />
                    <Button
                      icon={<EyeOutlined />}
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreviewLogo(logoUrl, record.name);
                      }}
                    >
                      Aperçu
                    </Button>
                  </div>
                );
              }}
            />
            <Table.Column
              title="Nom de l'établissement"
              dataIndex="name"
              key="name"
            />
            <Table.Column
              title="Entreprise"
              dataIndex="company_name"
              key="company_name"
              responsive={["md"]}
            />
            <Table.Column
              title="Adresse"
              dataIndex="address"
              key="address"
              responsive={["lg"]}
            />
            <Table.Column
              title="Téléphone"
              dataIndex="phone"
              key="phone"
              responsive={["lg"]}
            />
            <Table.Column
              title="Email"
              dataIndex="email"
              key="email"
              responsive={["md"]}
            />
            <Table.Column
              title="Statut"
              dataIndex="status"
              key="status"
              render={(text, record) => (
                <Switch
                  checkedChildren="Actif"
                  unCheckedChildren="Inactif"
                  checked={record.status === "active"}
                  onChange={() => handleToggleStatus(record)}
                  disabled={!user?.is_superadmin || !hasPermission("Admin.Magasins.edit")}
                />
              )}
            />
            <Table.Column
              title="Actions"
              key="actions"
              render={(text, record) => (
                <Space>
                  {hasPermission("Admin.Magasins.view") && (
                    <Tooltip title="Voir les détails">
                      <Button
                        icon={<EyeOutlined />}
                        onClick={() => handleViewWarehouseDetails(record)}
                      />
                    </Tooltip>
                  )}
                  {hasPermission("Admin.Magasins.edit") && (
                    <Tooltip title="Modifier">
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                          setSelectedWarehouse(record);
                          setIsModalOpen(true);
                        }}
                      />
                    </Tooltip>
                  )}
                  {user?.is_superadmin && hasPermission("Admin.Magasins.delete") && (
                    <Tooltip title="Supprimer">
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleDeleteWarehouse(record)}
                      />
                    </Tooltip>
                  )}
                </Space>
              )}
            />
          </Table>
        )}
      </div>

      {/* Styles CSS pour contrôler la hauteur des lignes */}
      <style jsx>{`
        .warehouse-table .warehouse-table-row {
          height: 64px !important;
        }

        .warehouse-table .logo-column {
          vertical-align: middle;
        }

        .logo-container {
          display: flex;
          align-items: center;
          height: 100%;
        }

        .empty-logo-placeholder {
          width: 40px;
          height: 40px;
          background-color: #f0f0f0;
          border-radius: 4px;
        }
      `}</style>

      <WarehouseModal
        key={selectedWarehouse ? selectedWarehouse.id : "new"}
        visible={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedWarehouse(null);
        }}
        warehouse={selectedWarehouse}
        companies={companies}
        onSubmit={
          selectedWarehouse ? handleUpdateWarehouse : handleCreateWarehouse
        }
        user={user}
        selectedCompany={selectedCompany}
        selectedCompanyName={getSelectedCompanyName()}
      />

      {/* Modal de prévisualisation du logo */}
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancelPreview}
        centered
        width={isSmallScreen ? "95%" : 600} // Responsive width
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <img
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "500px",
              objectFit: "contain",
              border: "1px solid #e8e8e8",
              borderRadius: "4px",
              padding: "10px",
              backgroundColor: "#f9f9f9",
            }}
            src={previewImage}
          />
        </div>
      </Modal>

      {/* Modal de détails du magasin */}
      <WarehouseDetailModal
        visible={detailModalVisible}
        onClose={handleCloseDetailModal}
        warehouse={warehouseDetails}
      />
    </div>
  );
};

export default GestionWarehouses;
